// AuroraBeforeAfterBridge — Tier-A "Aurora Drift" renderer for the
// before-after-bridge component (the world's SECOND home-set pairing, after
// confidence-meter).
//
// Reuses the existing Aurora toolkit registration (atmosphere + audio cue palette
// in lib/scenario-themes/) — no new world infra. The before/after SHIFT is Aurora
// Drift's signature beat (kin to the confidence-meter bloom): each comparison row
// blooms its AFTER cell in with a luminous aurora glow + a magnitude-free shimmer.
//
// State machine + onActivityResponse mirror BeforeAfterBridgeSlide's Student
// exactly: a `before → content → after → comparison` phase machine + `before` /
// `after` answer maps; on the after action it emits the EXACT
//   { activityType:'before-after-bridge', mode:'survey',
//     responseData:{ before, after } }
// payload and advances to the comparison. Saving the close's reflection
// re-emits the same composite record with a `reflection` field folded in
// (records are keyed per slide, so the write updates in place).
//
// Ending contract (parity with the standard skin, §8.5 audit fix): the
// comparison lands the shared <ExperienceClose> with the same token values
// (before / after / delta from the first scale prompt), scale rows show the
// computed signed shift, and the reflection prompt is a real captured commit.

import { useState, useCallback, useEffect, useRef } from 'react';
import type { BeforeAfterBridgeSlideData, Theme, ExperienceConclusion } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio, playAuroraPluck, playAuroraShiftBloom } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { ExperienceClose } from '../../shared/ExperienceClose';

type Phase = 'before' | 'content' | 'after' | 'comparison';

const STYLE_ID = 'aurora-bab-scoped-styles';

const GREEN = '#5ef2a0';
const TEAL = '#15c4b8';
const VIOLET = '#8b6cff';
const NIGHT = '#0a0e1f';
const FROST = '#eaf4ff';
const FROST_DIM = '#9fb3d9';

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.aurbab`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.aurbab { position: relative; }
.aurbab * { box-sizing: border-box; }

.aurbab .aur-glass {
  background: rgba(20,30,60,0.34); backdrop-filter: blur(20px) saturate(1.5); -webkit-backdrop-filter: blur(20px) saturate(1.5);
  border: 1px solid rgba(234,244,255,0.16); border-radius: 22px;
  box-shadow: 0 16px 50px rgba(0,0,0,0.32), inset 0 1px 0 rgba(234,244,255,0.07);
  position: relative;
}

/* phase tracker dots */
.aurbab .aur-track { display: flex; align-items: center; justify-content: center; gap: 10px; margin-bottom: 36px; }
.aurbab .aur-track-dot { display: flex; align-items: center; gap: 10px; }
.aurbab .aur-dot { width: 9px; height: 9px; border-radius: 50%; background: rgba(234,244,255,0.18); transition: background .4s, box-shadow .4s, transform .4s; }
.aurbab .aur-dot.done { background: ${TEAL}; }
.aurbab .aur-dot.current { background: ${GREEN}; box-shadow: 0 0 12px ${GREEN}; transform: scale(1.35); }
.aurbab .aur-dot-label { font-size: 0.7rem; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(234,244,255,0.4); }
.aurbab .aur-dot-label.current { color: ${FROST}; }
.aurbab .aur-track-line { width: 26px; height: 1px; background: rgba(234,244,255,0.16); }

/* inputs */
.aurbab textarea.aur-input {
  width: 100%; font-family: inherit; font-size: 1rem; color: ${FROST}; line-height: 1.55;
  background: rgba(10,14,31,0.4); border: 1px solid rgba(234,244,255,0.14); border-radius: 14px; padding: 12px 14px; resize: vertical;
  transition: border-color .3s, box-shadow .3s;
}
.aurbab textarea.aur-input:focus { outline: none; border-color: ${GREEN}; box-shadow: 0 0 0 3px rgba(94,242,160,0.14); }

.aurbab input[type="range"].aur-range { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; border-radius: 999px; outline: none; cursor: grab; border: 1px solid rgba(234,244,255,0.1); }
.aurbab input[type="range"].aur-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 24px; height: 24px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL}); border: 2px solid rgba(255,255,255,0.85); box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 20px rgba(94,242,160,0.65); transition: transform .2s; }
.aurbab input[type="range"].aur-range::-webkit-slider-thumb:hover { transform: scale(1.12); }
.aurbab input[type="range"].aur-range::-moz-range-thumb { width: 24px; height: 24px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.85); background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL}); box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 20px rgba(94,242,160,0.65); }
.aurbab .aur-scale-val { font-family: "Instrument Serif", Georgia, serif; font-size: 2.6rem; line-height: 1; color: ${FROST}; text-shadow: 0 0 24px rgba(94,242,160,0.5); text-align: center; }

.aurbab .aur-btn {
  width: 100%; margin-top: 8px; font-size: 0.95rem; font-weight: 500; letter-spacing: 0.02em; color: ${NIGHT}; cursor: pointer;
  padding: 14px 26px; border-radius: 999px; border: none; background: linear-gradient(110deg, ${GREEN}, ${TEAL});
  box-shadow: 0 6px 28px rgba(21,196,184,0.42); transition: transform .35s cubic-bezier(0.22,0.61,0.27,1), box-shadow .35s, opacity .3s;
}
.aurbab .aur-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 38px rgba(94,242,160,0.55); }
.aurbab .aur-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.aurbab .aur-hint { font-size: 0.86rem; font-style: italic; color: ${FROST_DIM}; background: rgba(139,108,255,0.1); border: 1px solid rgba(139,108,255,0.22); border-radius: 10px; padding: 8px 12px; margin-bottom: 12px; }

/* content blocks */
.aurbab .aur-quote { border-left: 3px solid ${VIOLET}; padding: 4px 0 4px 16px; }

/* comparison bloom */
.aurbab .aur-cmp { opacity: 0; transform: translateY(24px); animation: aurRise .8s cubic-bezier(0.22,0.61,0.27,1) forwards; }
@keyframes aurRise { to { opacity: 1; transform: translateY(0); } }
.aurbab .aur-after-cell { position: relative; overflow: hidden; }
.aurbab .aur-after-cell.bloom::before { content: ""; position: absolute; inset: 0; border-radius: inherit; background: radial-gradient(120% 120% at 0% 0%, rgba(94,242,160,0.22), transparent 55%), radial-gradient(120% 120% at 100% 100%, rgba(139,108,255,0.2), transparent 55%); animation: aurBloom 2.6s ease forwards; pointer-events: none; }
@keyframes aurBloom { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0.5; } }

/* authored close — shared <ExperienceClose> content, aurora glass container */
.aurbab .aur-close { opacity: 0; transform: translateY(24px); animation: aurRise .8s cubic-bezier(0.22,0.61,0.27,1) .3s forwards; margin-top: 26px; }
.aurbab .aur-close > div {
  background: rgba(20,30,60,0.5) !important;
  backdrop-filter: blur(20px) saturate(1.5); -webkit-backdrop-filter: blur(20px) saturate(1.5);
  border: 1px solid rgba(234,244,255,0.16) !important;
  border-left: 5px solid ${VIOLET} !important;
  box-shadow: 0 16px 50px rgba(0,0,0,0.32), inset 0 1px 0 rgba(234,244,255,0.07) !important;
}

@media (prefers-reduced-motion: reduce) {
  .aurbab .aur-cmp { animation-duration: .001s; }
  .aurbab .aur-close { animation-duration: .001s; animation-delay: 0s; }
  .aurbab .aur-after-cell.bloom::before { animation: none; opacity: 0.5; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

const PHASES: { key: Phase; label: string }[] = [
  { key: 'before', label: 'Before' },
  { key: 'content', label: 'Read' },
  { key: 'after', label: 'After' },
  { key: 'comparison', label: 'Shift' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function AuroraBeforeAfterBridge({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: BeforeAfterBridgeSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('aurora');
  const headingFont = theme.fonts.heading;

  // A saved { before, after } payload describes the finished end-state — restore
  // straight to the comparison view (mirrors the standard renderer).
  const savedBefore = savedResponse?.before as Record<string, string> | undefined;
  const savedAfter = savedResponse?.after as Record<string, string> | undefined;
  const restored = !!(savedBefore && typeof savedBefore === 'object' && savedAfter && typeof savedAfter === 'object');

  // Scale sliders (ScaleField) display a default of 3 even before the learner
  // touches them, so seed the recorded state with that default — display and
  // state must never diverge, or accepting the default leaves the gating button
  // disabled (the SCALE-INPUT DEADLOCK the standard renderer fixed). Mirrors
  // BeforeAfterBridgeStudent's seedScaleDefaults exactly.
  const seedScaleDefaults = (
    prompts: Array<{ id: string; inputType: 'text' | 'scale' }>,
  ): Record<string, string> =>
    Object.fromEntries(prompts.filter((p) => p.inputType === 'scale').map((p) => [p.id, '3']));

  // ── State machine (mirrors BeforeAfterBridgeStudent) ─────────────────────────
  const [phase, setPhase] = useState<Phase>(restored ? 'comparison' : 'before');
  const [before, setBefore] = useState<Record<string, string>>(() =>
    restored ? savedBefore! : seedScaleDefaults(data.beforePhase.prompts),
  );
  const [after, setAfter] = useState<Record<string, string>>(() =>
    restored
      ? savedAfter!
      : seedScaleDefaults(
          data.afterPhase.prompts.map((p) => ({
            id: p.id,
            inputType: data.beforePhase.prompts.find((bp) => bp.id === p.id)?.inputType ?? 'text',
          })),
        ),
  );

  const beforeReady = data.beforePhase.prompts.every((p) => (before[p.id] ?? '').trim().length > 0);
  const afterReady = data.afterPhase.prompts.every((p) => (after[p.id] ?? '').trim().length > 0);

  // Fire onComplete exactly once at the terminal after-phase submit (the only
  // point the activity is finished). Pre-armed when restored from a saved response.
  const completedRef = useRef(restored);

  const submit = useCallback(() => {
    onActivityResponse?.({ activityType: 'before-after-bridge', mode: 'survey', responseData: { before, after } });
    play('commit');
    setPhase('comparison');
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [before, after, onActivityResponse, onComplete, play]);

  // Saving the close's reflection re-emits the composite record with the
  // reflection folded in as an additional field — same capture call, keyed per
  // slide, so the write updates the { before, after } row in place.
  const commitReflection = useCallback(
    (text: string) => {
      onActivityResponse?.({
        activityType: 'before-after-bridge',
        mode: 'survey',
        responseData: { before, after, reflection: text },
      });
      play('commit');
    },
    [before, after, onActivityResponse, play],
  );

  // Close reflect-back (mirrors the standard skin's ComparisonView): the first
  // scale prompt's before/after feed the {before}/{after}/{delta} tokens so an
  // authored "your rating moved X → Y" close fills identically in both skins.
  // Text-only experiences have no numeric delta; unfilled tokens trigger the
  // token-free fallback line inside ExperienceClose.
  const scalePrompt = data.beforePhase.prompts.find((p) => p.inputType === 'scale');
  const closeBeforeNum = scalePrompt ? Number(before[scalePrompt.id]) : NaN;
  const closeAfterNum = scalePrompt ? Number(after[scalePrompt.id]) : NaN;
  const closeHasDelta = !Number.isNaN(closeBeforeNum) && !Number.isNaN(closeAfterNum);
  const closeValues = closeHasDelta
    ? { before: closeBeforeNum, after: closeAfterNum, delta: closeAfterNum - closeBeforeNum }
    : {};

  // Authored close only — no conclusion means no close (back-compat; the
  // legacy display card below then carries the reflection prompt). When a
  // conclusion IS authored, the legacy comparison.reflectionPrompt folds into
  // its optional reflection line unless the author supplied their own.
  const conclusion: ExperienceConclusion | undefined = data.conclusion
    ? { ...data.conclusion, reflectionPrompt: data.conclusion.reflectionPrompt ?? data.comparison?.reflectionPrompt }
    : undefined;

  // bloom chord when the comparison appears
  const bloomedRef = useRef(false);
  useEffect(() => {
    if (phase === 'comparison' && !bloomedRef.current) {
      bloomedRef.current = true;
      const t = window.setTimeout(() => playWith((e) => playAuroraShiftBloom(e, 1.1)), 360);
      return () => window.clearTimeout(t);
    }
  }, [phase, playWith]);

  const currentIdx = PHASES.findIndex((p) => p.key === phase);

  return (
    <div className="aurbab h-full w-full overflow-y-auto" style={{ background: NIGHT, color: FROST, fontFamily: theme.fonts.body }}>
      <ThemeWorldAtmosphere worldId="aurora" primary={TEAL} accent={VIOLET} background={NIGHT} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '84px 24px 110px' }}>
          {/* hero */}
          <header style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: '0.72rem', letterSpacing: '0.42em', textTransform: 'uppercase', color: TEAL, fontWeight: 500, marginBottom: 18 }}>
              Aurora Drift · The Shift
            </div>
            <h1 style={{ fontFamily: headingFont, fontWeight: 400, fontSize: 'clamp(2rem, 5.5vw, 3.4rem)', lineHeight: 1.06, color: FROST, margin: '0 0 14px' }}>
              {data.title}
            </h1>
            {data.subtitle && <p style={{ fontSize: '1.08rem', color: FROST_DIM, fontWeight: 300, margin: 0 }}>{data.subtitle}</p>}
          </header>

          {/* phase tracker */}
          <div className="aur-track">
            {PHASES.map((p, idx) => (
              <div key={p.key} className="aur-track-dot">
                <span className={`aur-dot${idx < currentIdx ? ' done' : ''}${idx === currentIdx ? ' current' : ''}`} />
                <span className={`aur-dot-label${idx === currentIdx ? ' current' : ''}`}>{p.label}</span>
                {idx < PHASES.length - 1 && <span className="aur-track-line" />}
              </div>
            ))}
          </div>

          {/* BEFORE */}
          {phase === 'before' && (
            <PromptPhase
              instruction={data.beforePhase.instruction}
              prompts={data.beforePhase.prompts.map((p) => ({ id: p.id, text: p.beforeText, inputType: p.inputType, maxLength: p.maxLength, scaleLabels: p.scaleLabels }))}
              values={before}
              onChange={(id, v) => setBefore((s) => ({ ...s, [id]: v }))}
              headingFont={headingFont}
              actionLabel="Lock it in & read on →"
              actionReady={beforeReady}
              onAction={() => { play('advance'); setPhase('content'); }}
              playPluck={(f) => playWith((e) => playAuroraPluck(e, f))}
            />
          )}

          {/* CONTENT */}
          {phase === 'content' && (
            <div>
              <p style={{ fontSize: '0.95rem', color: FROST_DIM, marginBottom: 18 }}>{data.contentPhase.instruction}</p>
              {data.contentPhase.blocks.map((block) => (
                <div key={block.id} className={block.type === 'quote' ? '' : 'aur-glass'} style={{ padding: block.type === 'quote' ? 0 : '20px 22px', marginBottom: 16 }}>
                  {block.type === 'quote' ? (
                    <blockquote className="aur-quote" style={{ margin: 0 }}>
                      <p style={{ fontFamily: headingFont, fontStyle: 'italic', fontSize: '1.2rem', lineHeight: 1.4, color: FROST, margin: 0 }}>“{block.content}”</p>
                      {block.source && <p style={{ fontSize: '0.8rem', color: FROST_DIM, marginTop: 8 }}>— {block.source}</p>}
                    </blockquote>
                  ) : (
                    <>
                      {block.type === 'data' && <div style={{ fontSize: '0.66rem', letterSpacing: '0.24em', textTransform: 'uppercase', color: VIOLET, fontWeight: 600, marginBottom: 8 }}>The evidence</div>}
                      <p style={{ fontSize: '1rem', lineHeight: 1.62, color: FROST, fontWeight: 300, margin: 0 }}>{block.content}</p>
                      {block.source && <p style={{ fontSize: '0.8rem', color: FROST_DIM, marginTop: 8 }}>{block.source}</p>}
                    </>
                  )}
                </div>
              ))}
              <button type="button" className="aur-btn" onClick={() => { play('advance'); setPhase('after'); }}>
                I&apos;ve read this — now, again ✦
              </button>
            </div>
          )}

          {/* AFTER */}
          {phase === 'after' && (
            <PromptPhase
              instruction={data.afterPhase.instruction}
              prompts={data.afterPhase.prompts.map((p) => {
                const bm = data.beforePhase.prompts.find((bp) => bp.id === p.id);
                return { id: p.id, text: p.afterText, inputType: bm?.inputType ?? 'text', maxLength: bm?.maxLength, scaleLabels: bm?.scaleLabels, hint: before[p.id] ? `You said before: “${before[p.id]}”` : undefined };
              })}
              values={after}
              onChange={(id, v) => setAfter((s) => ({ ...s, [id]: v }))}
              headingFont={headingFont}
              actionLabel="Reveal the shift ✦"
              actionReady={afterReady}
              onAction={submit}
              playPluck={(f) => playWith((e) => playAuroraPluck(e, f))}
            />
          )}

          {/* COMPARISON */}
          {phase === 'comparison' && (
            <div className="aur-cmp">
              <h2 style={{ fontFamily: headingFont, fontWeight: 400, fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', color: FROST, textAlign: 'center', margin: '0 0 8px' }}>
                What shifted
              </h2>
              <p style={{ color: FROST_DIM, textAlign: 'center', fontWeight: 300, marginBottom: 28 }}>Your view, before and after the evidence reached you.</p>

              {data.beforePhase.prompts.map((bp, idx) => {
                const beforeVal = before[bp.id] ?? '—';
                const afterVal = after[bp.id] ?? '—';
                const rowBefore = Number(beforeVal);
                const rowAfter = Number(afterVal);
                const rowHasDelta =
                  bp.inputType === 'scale' &&
                  beforeVal.trim() !== '' &&
                  afterVal.trim() !== '' &&
                  Number.isFinite(rowBefore) &&
                  Number.isFinite(rowAfter);
                return (
                  <div key={bp.id} className="aur-glass" style={{ padding: '22px 24px', marginBottom: 18 }}>
                    <p style={{ fontFamily: headingFont, fontStyle: 'italic', fontSize: '1.12rem', color: FROST, margin: '0 0 16px' }}>{bp.beforeText}</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                      <div style={{ padding: '14px 16px', borderRadius: 14, background: 'rgba(10,14,31,0.5)', border: '1px solid rgba(234,244,255,0.1)' }}>
                        <div style={{ fontSize: '0.66rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: FROST_DIM, marginBottom: 8 }}>Before</div>
                        <div style={{ fontSize: '0.98rem', color: FROST_DIM }}>{beforeVal}</div>
                      </div>
                      <div className={`aur-after-cell${!reduceMotion ? ' bloom' : ''}`} style={{ padding: '14px 16px', borderRadius: 14, background: 'rgba(139,108,255,0.12)', border: `1px solid rgba(94,242,160,0.3)`, animationDelay: `${idx * 0.15}s` }}>
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div style={{ fontSize: '0.66rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: GREEN, marginBottom: 8 }}>After</div>
                          <div style={{ fontSize: '0.98rem', color: FROST }}>{afterVal}</div>
                        </div>
                      </div>
                    </div>
                    {rowHasDelta && <ShiftRow before={rowBefore} after={rowAfter} />}
                  </div>
                );
              })}

              {/* Authored "So what?" close — the shared component carries the
                  content (token parity with the standard skin); the aurora glass
                  container carries the presentation. Renders nothing when no
                  conclusion is authored (back-compat). */}
              {conclusion && (
                <div className="aur-close">
                  <ExperienceClose
                    conclusion={conclusion}
                    theme={theme}
                    values={closeValues}
                    fallback="Look back at your before and after answers above."
                    onReflection={commitReflection}
                  />
                </div>
              )}

              {/* Legacy display card — only when no close is rendered, so the
                  reflection prompt is never shown twice. */}
              {!conclusion && data.comparison?.reflectionPrompt && (
                <div className="aur-glass" style={{ padding: '24px 26px', textAlign: 'center', marginTop: 8 }}>
                  <div style={{ fontSize: '0.68rem', letterSpacing: '0.26em', textTransform: 'uppercase', color: VIOLET, fontWeight: 600, marginBottom: 10 }}>A gentle reflection</div>
                  <p style={{ fontFamily: headingFont, fontSize: '1.2rem', fontStyle: 'italic', color: FROST, lineHeight: 1.4, margin: 0 }}>{data.comparison.reflectionPrompt}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shift readout — the computed signed delta for a scale row (kin to the
// confidence-meter flagship's ShiftReadout: gradient serif numbers + a drift
// phrase in the world's own language).
// ─────────────────────────────────────────────────────────────────────────────

function shiftPhrase(delta: number): string {
  const mag = Math.abs(delta);
  if (mag >= 2) return 'a real shift, the evidence reached you';
  if (mag === 1) return 'a gentle drift';
  return 'you held steady, and that is fine too';
}

function ShiftRow({ before, after }: { before: number; after: number }) {
  const delta = after - before;
  const mag = Math.abs(delta);
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '±';
  const moved = delta !== 0;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
      <span
        style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: '1.6rem',
          fontStyle: 'italic',
          lineHeight: 1,
          background: `linear-gradient(100deg, ${GREEN}, ${TEAL}, ${VIOLET})`,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        {before} → {after}
      </span>
      <span
        style={{
          fontSize: '0.82rem',
          fontWeight: 600,
          color: moved ? GREEN : FROST_DIM,
          background: moved ? 'rgba(94,242,160,0.14)' : 'rgba(234,244,255,0.08)',
          border: `1px solid ${moved ? 'rgba(94,242,160,0.35)' : 'rgba(234,244,255,0.14)'}`,
          borderRadius: 999,
          padding: '3px 10px',
        }}
      >
        {sign}{mag}
      </span>
      <span style={{ fontSize: '0.86rem', fontWeight: 300, color: FROST_DIM }}>{shiftPhrase(delta)}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Prompt phase (before / after share this)
// ─────────────────────────────────────────────────────────────────────────────

interface PromptVM {
  id: string;
  text: string;
  inputType: 'text' | 'scale';
  maxLength?: number;
  scaleLabels?: [string, string];
  hint?: string;
}

function PromptPhase({
  instruction,
  prompts,
  values,
  onChange,
  headingFont,
  actionLabel,
  actionReady,
  onAction,
  playPluck,
}: {
  instruction: string;
  prompts: PromptVM[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  headingFont: string;
  actionLabel: string;
  actionReady: boolean;
  onAction: () => void;
  playPluck: (frac: number) => void;
}) {
  return (
    <div>
      {instruction && <p style={{ fontSize: '0.95rem', color: FROST_DIM, marginBottom: 18 }}>{instruction}</p>}
      {prompts.map((p) => (
        <div key={p.id} className="aur-glass" style={{ padding: '22px 24px', marginBottom: 16 }}>
          <label style={{ display: 'block', fontFamily: headingFont, fontStyle: 'italic', fontSize: '1.18rem', color: FROST, marginBottom: 14, lineHeight: 1.3 }}>{p.text}</label>
          {p.hint && <div className="aur-hint">{p.hint}</div>}
          {p.inputType === 'scale' ? (
            <ScaleField value={values[p.id] ?? ''} labels={p.scaleLabels ?? ['Low', 'High']} onChange={(v) => onChange(p.id, v)} playPluck={playPluck} />
          ) : (
            <>
              <textarea className="aur-input" rows={3} maxLength={p.maxLength ?? 300} value={values[p.id] ?? ''} onChange={(e) => onChange(p.id, e.target.value)} />
              <div style={{ textAlign: 'right', fontSize: '0.74rem', color: FROST_DIM, marginTop: 4 }}>{(values[p.id] ?? '').length} / {p.maxLength ?? 300}</div>
            </>
          )}
        </div>
      ))}
      <button type="button" className="aur-btn" disabled={!actionReady} onClick={onAction}>{actionLabel}</button>
    </div>
  );
}

function ScaleField({ value, labels, onChange, playPluck }: { value: string; labels: [string, string]; onChange: (v: string) => void; playPluck: (frac: number) => void }) {
  const num = Number(value) || 3;
  const pct = ((num - 1) / 4) * 100;
  const last = useRef(num);
  const fill = `linear-gradient(90deg, rgba(94,242,160,0.9) 0%, rgba(21,196,184,0.85) ${pct}%, rgba(234,244,255,0.12) ${pct}%, rgba(234,244,255,0.1) 100%)`;
  return (
    <div>
      <div className="aur-scale-val">{num}</div>
      <input
        type="range" className="aur-range" min={1} max={5} value={num} style={{ background: fill, marginTop: 10 }}
        onChange={(e) => {
          const v = Number(e.target.value);
          onChange(String(v));
          if (v !== last.current) { playPluck((v - 1) / 4); last.current = v; }
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: '0.74rem', color: FROST_DIM }}>
        <span>{labels[0]}</span>
        <span>{labels[1]}</span>
      </div>
    </div>
  );
}
